<template>
	<section :class="['marketing-section', panelBackgroundColor, panelTextColor]" :style="panelBackgroundStyle">
		<div class="container display-flex justify-content-center">
			<div class="col-6-lg col-9-md text-align-center">
				<h1 :class="['heading-xl', panelTextColor]" data-test="venture-capital-cta-heading">{{ heading }}</h1>
				<p
					v-if="description"
					class="mt-200"
					data-test="venture-capital-cta-description"
					v-html="deorphanText(description)"
				/>
				<MarketingHeroCta
					class="mt-250"
					:form-config="leadCaptureFormConfig"
					component-id="venture"
					:offering-id="growthTechReitId"
					:is-dark-mode="true"
				/>
				<p v-if="props.isFundExpensed" :class="['body-sm body-xs-smo text-align-center-smo', panelTextColor]"
					>Start investing in less than 5 minutes and with as little as $10.</p
				>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue';
import { ventureCapitalExpensedFunnelPlanId, ventureCapitalPresetPlanId } from '@constants/plans-info';
import { deorphanText } from '@filters/shared-filters';
import { getVentureMarketingPageCta } from '@api/sanity/graphql/venture-marketing-page';
import { growthTechReitId } from '@constants/reit';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import MarketingHeroCta from '@components/marketing-site/marketing-hero-cta.vue';
import { SanityMarketingCtaPanel } from 'types/sanity/marketing-components';

interface Props {
	isFundExpensed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isFundExpensed: false
});

const leadCaptureFormConfig = computed((): LeadCaptureFormConfig => {
	return {
		buttonText: sanityVentureCta.value?.ctaLinkText ?? 'Get started',
		signupFlowType: props.isFundExpensed ? 'FUND_MARKETING' : 'PRESET_PLAN',
		centerButton: true,
		presetPlanId: props.isFundExpensed ? ventureCapitalExpensedFunnelPlanId : ventureCapitalPresetPlanId,
		thirdPartyAuthTheme: 'SECONDARY'
	};
});

const sanityVentureCta: Ref<SanityMarketingCtaPanel | null> = ref(null);

const sanityHeading = computed((): string => sanityVentureCta.value?.ctaHeading ?? 'Invest in innovation');
const panelTextColor = computed((): string => (sanityVentureCta.value?.ctaDarkTheme ? 'dark' : 'text-color-white'));
const panelBackgroundColor = computed((): string =>
	sanityVentureCta.value?.ctaBackgroundColor?.hex ? 'bg-color-sanity-custom' : 'bg-color-content-primary'
);
const panelBackgroundStyle = computed((): string => {
	return sanityVentureCta.value?.ctaBackgroundColor?.hex
		? `background-color: ${sanityVentureCta.value.ctaBackgroundColor?.hex}`
		: '';
});

const heading = computed((): string =>
	props.isFundExpensed ? 'Add venture capital to your portfolio' : sanityHeading.value
);
const description = computed((): string => (props.isFundExpensed ? '' : sanityVentureCta.value?.ctaSummary ?? ''));

async function setup(): Promise<void> {
	try {
		const ctaResponse = await getVentureMarketingPageCta();
		sanityVentureCta.value = ctaResponse;
	} catch (e) {}
}
setup();
</script>
<style lang="scss" scoped>
@use '../../../styles/utilities/respond-to.scss' as *;
.marketing-section {
	@include respond-to(md) {
		padding-top: 8rem;
		padding-bottom: 8rem;
	}
}
</style>
